import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

const ORANGE = '#F18A00';
const BLACK  = '#161616';
const TEXT   = '#161616';
const MUTED  = '#64748B';
const BORDER = '#E5E7EB';

const SvgIcon = ({ size = 16, color = 'currentColor', children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);

function authFetch(url, opts = {}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { 'Content-Type': 'application/json', ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
}

const EMPTY_CONTACT = { nom: '', prenom: '', telephone: '', email: '' };

export default function FichePrestataire() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useLanguage();
  const { isReadOnly } = useAuth();
  const isNew = !id || id === 'nouveau';

  const [tab, setTab] = useState('infos');
  const [data, setData] = useState({
    nom: '', adresse_1: '', adresse_2: '', code_postal: '', ville: '', pays: 'France',
    numero_entreprise: '', numero_compte: '', iban: '', swift: '', notes: '',
    contacts: [{ ...EMPTY_CONTACT }],
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [saved, setSaved]     = useState(false);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const r = await authFetch(`/api/prestataires/${id}`);
      if (!r.ok) throw new Error(t('common.not_found'));
      const d = await r.json();
      setData({
        ...d,
        contacts: d.contacts && d.contacts.length ? d.contacts : [{ ...EMPTY_CONTACT }],
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [id, isNew, t]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!data.nom?.trim()) { setError(t('prestataires_section.err_nom')); return; }
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const payload = { ...data };
      // Nettoyer les contacts vides
      payload.contacts = (payload.contacts || []).filter(c =>
        (c.nom || c.prenom || c.email || c.telephone) && (c.nom || c.prenom || c.email)
      );

      const url = isNew ? '/api/prestataires' : `/api/prestataires/${id}`;
      const method = isNew ? 'POST' : 'PUT';
      const r = await authFetch(url, { method, body: JSON.stringify(payload) });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || t('common.error'));
      }
      const saved = await r.json();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (isNew) navigate(`/prestataires/${saved.id}`);
      else load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const addContact = () => setData(d => ({ ...d, contacts: [...(d.contacts || []), { ...EMPTY_CONTACT }] }));
  const removeContact = (i) => setData(d => ({ ...d, contacts: d.contacts.filter((_, idx) => idx !== i) }));
  const updateContact = (i, field, value) => setData(d => ({
    ...d,
    contacts: d.contacts.map((c, idx) => idx === i ? { ...c, [field]: value } : c),
  }));

  const TABS = [
    { id: 'infos',    label: t('prestataires_section.tab_infos') },
    { id: 'contacts', label: t('prestataires_section.tab_contacts') },
    { id: 'bank',     label: t('prestataires_section.tab_bank') },
  ];

  if (loading) return <div style={{ padding:32, color: MUTED }}>{t('common.loading')}</div>;

  return (
    <div>
      {/* En-tête noir */}
      <div style={{ background:BLACK, borderRadius:12, padding:'18px 22px', marginBottom:16,
        display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:13 }}>
          <button onClick={() => navigate('/prestataires')}
            style={{ width:32, height:32, borderRadius:8, border:'1px solid rgba(255,255,255,.2)',
              background:'rgba(255,255,255,.08)', color:'#fff', cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center' }}>
            <SvgIcon size={16} color="#fff"><path d="M5 12l14 0M5 12l6 6M5 12l6 -6"/></SvgIcon>
          </button>
          <div>
            <div style={{ fontSize:11, color:'#9A9A9A', letterSpacing:'.06em', textTransform:'uppercase' }}>
              {t('prestataires_section.breadcrumb')}
            </div>
            <div style={{ fontSize:18, fontWeight:500, color:'#fff' }}>
              {isNew ? t('prestataires_section.new_title') : (data.nom || '—')}
            </div>
          </div>
        </div>
        {!isReadOnly && (
        <button onClick={handleSave} disabled={saving}
          style={{ background:ORANGE, color:BLACK, border:'none', borderRadius:8, padding:'10px 22px',
            fontSize:14, cursor: saving ? 'not-allowed' : 'pointer', fontWeight:500,
            opacity: saving ? .6 : 1 }}>
          {saving ? t('common.saving') : t('common.save')}
        </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, marginBottom:16, borderBottom:`1px solid ${BORDER}` }}>
        {TABS.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
            style={{
              padding:'10px 18px', background:'transparent', border:'none',
              borderBottom: tab === tb.id ? `2px solid ${ORANGE}` : '2px solid transparent',
              color: tab === tb.id ? TEXT : MUTED,
              fontWeight: tab === tb.id ? 600 : 400,
              fontSize:14, cursor:'pointer', marginBottom:-1,
            }}>
            {tb.label}
          </button>
        ))}
      </div>

      {/* Messages */}
      {error && (
        <div style={{ marginBottom:12, padding:'10px 14px', background:'#FEE2E2', border:'1px solid #FECACA',
          borderRadius:8, color:'#7F1D1D', fontSize:13 }}>{error}</div>
      )}
      {saved && (
        <div style={{ marginBottom:12, padding:'10px 14px', background:'#D1FAE5', border:'1px solid #A7F3D0',
          borderRadius:8, color:'#065F46', fontSize:13 }}>✓ {t('utilisateurs.saved')}</div>
      )}

      {/* Tab content */}
      <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${BORDER}`, padding:24 }}>
        {tab === 'infos' && <TabInfos data={data} setData={setData} t={t} />}
        {tab === 'contacts' && (
          <TabContacts contacts={data.contacts || []} addContact={addContact} removeContact={removeContact} updateContact={updateContact} t={t} />
        )}
        {tab === 'bank' && <TabBank data={data} setData={setData} t={t} />}
      </div>
    </div>
  );
}

// ── Sous-composants ─────────────────────────────────────────────────────────

function Field({ label, children, required }) {
  return (
    <div>
      <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#475569', marginBottom:6 }}>
        {label}{required && <span style={{ color:'#DC2626', marginLeft:3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}
const inputStyle = { width:'100%', padding:'10px 12px', borderRadius:8, border:'1px solid #D9D9D5', fontSize:14, fontFamily:'inherit', outline:'none', background:'#fff', boxSizing:'border-box' };

function TabInfos({ data, setData, t }) {
  const set = (k) => (e) => setData(d => ({ ...d, [k]: e.target.value }));
  const PAYS_LIST = ['France','Belgique','Allemagne','Espagne','Italie','Pays-Bas','Suède','Suisse','Royaume-Uni','Portugal'];
  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
      <div style={{ gridColumn:'span 2' }}>
        <Field label={t('prestataires_section.field_nom')} required>
          <input style={inputStyle} value={data.nom||''} onChange={set('nom')} />
        </Field>
      </div>
      <Field label={t('prestataires_section.field_numero_entreprise')}>
        <input style={inputStyle} value={data.numero_entreprise||''} onChange={set('numero_entreprise')} placeholder={t('prestataires_section.numero_placeholder')} />
      </Field>
      <Field label={t('common.country')}>
        <select style={{...inputStyle, cursor:'pointer'}} value={data.pays||'France'} onChange={set('pays')}>
          {PAYS_LIST.map(p => <option key={p}>{p}</option>)}
        </select>
      </Field>
      <div style={{ gridColumn:'span 2' }}>
        <Field label={t('prestataires_section.field_adresse_1')}>
          <input style={inputStyle} value={data.adresse_1||''} onChange={set('adresse_1')} placeholder={t('prestataires_section.adresse_placeholder')} />
        </Field>
      </div>
      <div style={{ gridColumn:'span 2' }}>
        <Field label={t('prestataires_section.field_adresse_2')}>
          <input style={inputStyle} value={data.adresse_2||''} onChange={set('adresse_2')} />
        </Field>
      </div>
      <Field label={t('prestataires_section.field_code_postal')}>
        <input style={inputStyle} value={data.code_postal||''} onChange={set('code_postal')} />
      </Field>
      <Field label={t('prestataires_section.field_ville')}>
        <input style={inputStyle} value={data.ville||''} onChange={set('ville')} />
      </Field>
      <div style={{ gridColumn:'span 2' }}>
        <Field label={t('prestataires_section.field_notes')}>
          <textarea style={{...inputStyle, minHeight:80, resize:'vertical'}} value={data.notes||''} onChange={set('notes')} placeholder={t('prestataires_section.notes_placeholder')} />
        </Field>
      </div>
    </div>
  );
}

function TabContacts({ contacts, addContact, removeContact, updateContact, t }) {
  return (
    <div>
      {contacts.map((c, i) => (
        <div key={i} style={{ padding:16, background:'#F8FAFC', borderRadius:8, marginBottom:12, border:'1px solid #E5E7EB' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#475569' }}>
              {t('clients.contact_number')} {i + 1}
              {i === 0 && <span style={{ marginLeft:8, color:'#0F6E56', fontSize:11 }}>· {t('clients.main_contact')}</span>}
            </div>
            {i > 0 && (
              <button onClick={() => removeContact(i)}
                style={{ fontSize:12, color:'#DC2626', background:'none', border:'none', cursor:'pointer' }}>
                {t('common.delete')}
              </button>
            )}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label={t('clients.first_name')}>
              <input style={inputStyle} value={c.prenom||''} onChange={e => updateContact(i, 'prenom', e.target.value)} />
            </Field>
            <Field label={t('clients.last_name')}>
              <input style={inputStyle} value={c.nom||''} onChange={e => updateContact(i, 'nom', e.target.value)} />
            </Field>
            <Field label={t('clients.email')}>
              <input style={inputStyle} type="email" value={c.email||''} onChange={e => updateContact(i, 'email', e.target.value)} />
            </Field>
            <Field label={t('clients.phone')}>
              <input style={inputStyle} value={c.telephone||''} onChange={e => updateContact(i, 'telephone', e.target.value)} />
            </Field>
          </div>
        </div>
      ))}
      <button onClick={addContact}
        style={{ padding:'10px 16px', background:'#fff', border:`1px dashed ${ORANGE}`, color:ORANGE,
          borderRadius:8, cursor:'pointer', fontWeight:500, fontSize:13 }}>
        {t('clients.add_contact')}
      </button>
    </div>
  );
}

function TabBank({ data, setData, t }) {
  const set = (k) => (e) => setData(d => ({ ...d, [k]: e.target.value }));
  return (
    <div>
      <div style={{ padding:'12px 14px', background:'#FEF3C7', borderRadius:8, marginBottom:16, fontSize:12, color:'#92400E', display:'flex', alignItems:'center', gap:8 }}>
        <SvgIcon size={14} color="#92400E"><path d="M12 9v4M12 17h.01M10.29 3.86l-8.47 14.14a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71 -3l-8.47 -14.14a2 2 0 0 0 -3.42 0z"/></SvgIcon>
        {t('prestataires_section.bank_warning')}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        <Field label={t('prestataires_section.field_numero_compte')}>
          <input style={inputStyle} value={data.numero_compte||''} onChange={set('numero_compte')} />
        </Field>
        <Field label={t('prestataires_section.field_swift')}>
          <input style={inputStyle} value={data.swift||''} onChange={set('swift')} placeholder="ABCDFR2L" />
        </Field>
        <div style={{ gridColumn:'span 2' }}>
          <Field label={t('prestataires_section.field_iban')}>
            <input style={{...inputStyle, fontFamily:'monospace', letterSpacing:'1px'}}
              value={data.iban||''} onChange={set('iban')} placeholder="FR76 0000 0000 0000 0000 0000 000" />
          </Field>
        </div>
      </div>
    </div>
  );
}
